import React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import RecaptchaCheckbox from '../../components/common/RecaptchaCheckbox';
import { submitBrevoForm } from '../../services/BrevoService';
import { loadRecaptcha as defaultLoadRecaptcha } from '../../common/recaptcha';
import { CONTACT_EMAIL } from '../../common/constants';
import {
  EMPTY_VALUES, MAX_LENGTHS, prefillFromUser, validate, toBrevoFields, mailtoFallback, messageLength,
} from '../../common/earlyAccess';

// Page order, and the order focus moves through after a failed submit.
// 'captcha' has no input to focus; it only shows its message.
const FIELD_ORDER = ['email', 'firstName', 'lastName', 'organization', 'username', 'message', 'captcha'];

const TEXT_FIELDS = [
  { name: 'email', labelKey: 'email', type: 'email', autoComplete: 'email', required: true },
  { name: 'firstName', labelKey: 'first_name', autoComplete: 'given-name', required: true },
  { name: 'lastName', labelKey: 'last_name', autoComplete: 'family-name', required: true },
  { name: 'organization', labelKey: 'organization', autoComplete: 'organization', required: true },
  // Optional (Jonathan, 2026-09-25, D13): prefilled from the signed-in
  // profile but never required, and editable so a visitor can correct or
  // clear it. Placed after Organization.
  { name: 'username', labelKey: 'username', autoComplete: 'off', required: false, hintKey: 'username_hint' },
];

const PREFILLED = ['email', 'firstName', 'lastName', 'organization', 'username'];

// The /early-access form (ocl_online#170). Every visible field is required
// except OCL username (Jonathan, 2026-09-24; username made optional 2026-09-25,
// D13). It posts straight to Brevo, which verifies the reCAPTCHA token; on
// any failure the visitor gets a mailto: carrying what they typed, so
// nothing is lost.
const EarlyAccessForm = ({ formUrl, siteKey, source, user, onSuccess, loadRecaptcha = defaultLoadRecaptcha }) => {
  const { t } = useTranslation();
  const [values, setValues] = React.useState({ ...EMPTY_VALUES });
  const [errors, setErrors] = React.useState({});
  const [token, setToken] = React.useState(null);
  const [captchaFailed, setCaptchaFailed] = React.useState(false);
  const [status, setStatus] = React.useState('idle');
  const form = React.useRef(null);
  const recaptcha = React.useRef(null);

  // The profile arrives after mount (the page reads localStorage in an
  // effect), so only fill fields the visitor hasn't typed into yet.
  React.useEffect(() => {
    if (!user)
      return;
    const prefill = prefillFromUser(user);
    setValues(current => {
      const next = { ...current };
      PREFILLED.forEach(field => {
        if (!current[field] && prefill[field])
          next[field] = prefill[field];
      });
      return next;
    });
  }, [user]);

  // Clears a field's own error as soon as the visitor changes it, rather
  // than leaving stale "Required" text on screen until the next submit.
  const set = field => event => {
    const { value } = event.target;
    setValues(current => ({ ...current, [field]: value }));
    setErrors(current => (current[field] ? { ...current, [field]: undefined } : current));
  };
  const errorText = field => errors[field] && t(`early_access.errors.${errors[field]}`, { max: MAX_LENGTHS[field] });

  // A fresh token means the visitor ticked the box, so the captcha error (if
  // any) no longer applies; expiry/error (token === null) leaves it alone —
  // there's nothing new to report until they act again.
  const onCaptchaToken = tok => {
    setToken(tok);
    if (tok)
      setErrors(current => (current.captcha ? { ...current, captcha: undefined } : current));
  };

  const onSubmit = async event => {
    event.preventDefault();
    if (status === 'submitting')
      return;
    const found = validate(values);
    if (!token)
      found.captcha = 'captcha';
    setErrors(found);
    const first = FIELD_ORDER.find(field => found[field]);
    if (first) {
      const input = form.current.querySelector(`[name="${first}"]`);
      if (input)
        input.focus();
      return;
    }

    setStatus('submitting');
    const result = await submitBrevoForm(formUrl, toBrevoFields(values, { source, captchaToken: token }));
    if (result.ok) {
      onSuccess({ email: values.email.trim() });
      return;
    }
    setStatus('error');
    if (recaptcha.current)
      recaptcha.current.reset();
  };

  const mailto = mailtoFallback(values, CONTACT_EMAIL);
  // Each line break counts as 2 toward the cap (D14), so the raw HTML
  // maxlength has to shrink by the line breaks already typed, or the
  // visitor could type past the weighted limit.
  const messageMaxLength = MAX_LENGTHS.message - (values.message.match(/\n/g) || []).length;

  return (
    <Box component="form" ref={form} noValidate onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {TEXT_FIELDS.map(field => (
        <TextField
          key={field.name}
          id={`early-access-${field.name}`}
          name={field.name}
          type={field.type || 'text'}
          autoComplete={field.autoComplete}
          label={t(`early_access.fields.${field.labelKey}`)}
          value={values[field.name]}
          onChange={set(field.name)}
          error={Boolean(errors[field.name])}
          helperText={errorText(field.name) || (field.hintKey && t(`early_access.fields.${field.hintKey}`))}
          required={field.required}
          fullWidth
          slotProps={MAX_LENGTHS[field.name] ? { htmlInput: { maxLength: MAX_LENGTHS[field.name] } } : undefined}
        />
      ))}

      <TextField
        id="early-access-message"
        name="message"
        label={t('early_access.fields.message')}
        value={values.message}
        onChange={set('message')}
        error={Boolean(errors.message)}
        helperText={
          <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <span>{errorText('message') || t('early_access.fields.message_hint')}</span>
            <span>{`${messageLength(values.message)}/${MAX_LENGTHS.message}`}</span>
          </Box>
        }
        required
        fullWidth
        multiline
        minRows={4}
        slotProps={{ htmlInput: { maxLength: messageMaxLength } }}
      />

      {captchaFailed ? (
        // No action button here: the submit button below becomes the one
        // "Email your request" action, so there's a single clear next step
        // instead of two competing buttons (final-review.md M3).
        <Alert severity="warning">
          {t('early_access.captcha_blocked')}
        </Alert>
      ) : (
        <Box>
          <RecaptchaCheckbox ref={recaptcha} siteKey={siteKey} onToken={onCaptchaToken} onLoadError={() => setCaptchaFailed(true)} load={loadRecaptcha} />
          {errors.captcha && <FormHelperText error role="alert">{errorText('captcha')}</FormHelperText>}
        </Box>
      )}

      {status === 'error' && (
        <Alert severity="error" action={<Button color="inherit" size="small" href={mailto}>{t('early_access.email_request')}</Button>}>
          <strong>{t('early_access.failed_title')}</strong> {t('early_access.failed_text')}
        </Alert>
      )}

      <Box>
        {captchaFailed ? (
          // reCAPTCHA never loaded, so there's no token to submit with — a
          // "Request early access" button here would look live and do
          // nothing (final-review.md M3). Offer the working path instead.
          <Button variant="contained" disableElevation href={mailto}>{t('early_access.email_request')}</Button>
        ) : (
          <Button type="submit" variant="contained" disableElevation loading={status === 'submitting'}>
            {t('early_access.submit')}
          </Button>
        )}
      </Box>

      <Typography variant="body2" sx={{ color: 'surface.contrastText' }}>
        {t('early_access.privacy_note')}{' '}
        <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer">{t('early_access.privacy_link')}</Link>
      </Typography>
    </Box>
  );
};

export default EarlyAccessForm;
